/**
 * Pure arithmetic for the /pricing savings calculator. Takes validated inputs plus the live
 * per-unit plan price (from GET /api/public/pricing) and returns every line in cents. No prices,
 * copy or rendering live here.
 */
import { ACH_PAYMENT_FEE_CENTS } from '@resida/api-types/plan-copy';
import { SAVINGS_LIMITS, type SavingsFieldKey } from './pricing-content';

export interface SavingsInputs {
  units: number;
  rentCents: number;
  /** 0-100: share of rent payments made by Zelle or External Payments (no payment fee). */
  zelleSharePercent: number;
  managementFeePercent: number;
  hoursPerUnit: number;
  hourlyValueCents: number;
  esignMonthlyCents: number;
}

export interface SavingsResult {
  managerCents: number;
  timeCents: number;
  esignCents: number;
  savingsCents: number;
  platformCents: number;
  achCents: number;
  costCents: number;
  netMonthlyCents: number;
  netAnnualCents: number;
}

/** Raw text of each calculator field, as typed. */
export type SavingsRawInputs = Record<SavingsFieldKey, string>;

function parseNumber(raw: string): number | null {
  if (raw.trim() === '') return null;
  const value = Number(raw);
  return Number.isFinite(value) && value >= 0 ? value : null;
}

/** Validates the typed fields; null when any is empty, negative, non-numeric or out of range. */
export function parseSavingsInputs(raw: SavingsRawInputs): SavingsInputs | null {
  const units = parseNumber(raw.units);
  const rent = parseNumber(raw.rent);
  const zelleShare = parseNumber(raw.zelleShare);
  const managementFee = parseNumber(raw.managementFee);
  const hoursPerUnit = parseNumber(raw.hoursPerUnit);
  const hourlyValue = parseNumber(raw.hourlyValue);
  const esignMonthly = parseNumber(raw.esignMonthly);
  if (
    units === null ||
    rent === null ||
    zelleShare === null ||
    managementFee === null ||
    hoursPerUnit === null ||
    hourlyValue === null ||
    esignMonthly === null
  ) {
    return null;
  }
  if (!Number.isInteger(units) || units < SAVINGS_LIMITS.minUnits || units > SAVINGS_LIMITS.maxUnits) return null;
  if (zelleShare > 100 || managementFee > 100) return null;
  return {
    units,
    rentCents: Math.round(rent * 100),
    zelleSharePercent: zelleShare,
    managementFeePercent: managementFee,
    hoursPerUnit,
    hourlyValueCents: Math.round(hourlyValue * 100),
    esignMonthlyCents: Math.round(esignMonthly * 100),
  };
}

export function computeSavings(inputs: SavingsInputs, unitPriceCents: number): SavingsResult {
  const managerCents = Math.round(inputs.units * inputs.rentCents * (inputs.managementFeePercent / 100));
  const timeCents = Math.round(inputs.units * inputs.hoursPerUnit * inputs.hourlyValueCents);
  const esignCents = inputs.esignMonthlyCents;
  const savingsCents = managerCents + timeCents + esignCents;

  const platformCents = inputs.units * unitPriceCents;
  const achCents = Math.round(
    inputs.units * (1 - inputs.zelleSharePercent / 100) * ACH_PAYMENT_FEE_CENTS,
  );
  const costCents = platformCents + achCents;

  const netMonthlyCents = savingsCents - costCents;
  return {
    managerCents,
    timeCents,
    esignCents,
    savingsCents,
    platformCents,
    achCents,
    costCents,
    netMonthlyCents,
    netAnnualCents: netMonthlyCents * 12,
  };
}

const usd = (fractionDigits: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
const wholeDollars = usd(0);
const dollarsAndCents = usd(2);

/** Dollars, with cents only when the amount is not whole, so every shown line adds up to its total. */
export function formatMoneyCents(cents: number): string {
  return (cents % 100 === 0 ? wholeDollars : dollarsAndCents).format(cents / 100);
}
